/**
 * BossUsers - Gestion des utilisateurs Boss Ton Concours
 * Colonnes : total_quizzes, total_points, is_premium, subscription_plan/end_date, current_streak, rank
 * Actions : gérer abonnement, appliquer code promo
 */
import {
  AlertCircle,
  CheckCircle,
  Flame,
  Loader2,
  RefreshCw,
  Search,
  Star,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface BossUser {
  id: string;
  full_name: string;
  email: string;
  is_premium: boolean;
  subscription_plan: 'free' | 'single' | 'multi' | 'unlimited';
  subscription_end_date?: string;
  total_quizzes: number;
  total_points: number;
  current_streak: number;
  rank?: number;
  created_at: string;
}

const PLAN_STYLES: Record<string, string> = {
  free: 'bg-gray-100 text-gray-600',
  single: 'bg-blue-100 text-blue-600',
  multi: 'bg-indigo-100 text-indigo-600',
  unlimited: 'bg-purple-100 text-purple-600',
};

const BossUsers: React.FC = () => {
  const [users, setUsers] = useState<BossUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<BossUser | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const LIMIT = 20;

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT), search });
      const res = await authFetch(`${API_BASE_URL}/api/boss/users?${params}`);
      if (!res.ok) throw new Error('Erreur chargement utilisateurs Boss');
      const data = await res.json();
      setUsers(data.users ?? []);
      setTotal(data.total ?? 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, [page, search]);

  const applyPromo = async (userId: string) => {
    if (!promoCode.trim()) return;
    setActionLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/boss/users/${userId}/promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promo_code: promoCode }),
      });
      if (!res.ok) throw new Error('Code promo invalide ou expiré');
      const data = await res.json();
      setMessage({ type: 'success', text: `Code appliqué : ${data.plan ?? ''} jusqu'au ${data.end_date ?? '?'}` });
      setPromoCode('');
      loadUsers();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const revokeSubscription = async (userId: string) => {
    if (!confirm('Résilier l\'abonnement de cet utilisateur ?')) return;
    setActionLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/boss/users/${userId}/subscription`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erreur lors de la résiliation');
      setMessage({ type: 'success', text: 'Abonnement résilié' });
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-5 h-5 text-blue-500" />
            <h1 className="text-2xl font-bold text-gray-900">Utilisateurs Boss</h1>
          </div>
          <p className="text-gray-500 text-sm">Abonnements, quiz, streak, classement — Boss Ton Concours</p>
        </div>
        <button onClick={loadUsers} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Plans expliqués */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <h3 className="font-semibold text-blue-800 mb-2 text-sm">📦 Plans d'abonnement Boss</h3>
        <div className="grid md:grid-cols-4 gap-2 text-xs">
          <div className="bg-gray-100 text-gray-700 rounded p-2"><strong>Free</strong> — Quiz limités, accès basique</div>
          <div className="bg-blue-100 text-blue-700 rounded p-2"><strong>Single</strong> — 1 matière, quiz illimités</div>
          <div className="bg-indigo-100 text-indigo-700 rounded p-2"><strong>Multi</strong> — Toutes matières, challenges</div>
          <div className="bg-purple-100 text-purple-700 rounded p-2"><strong>Unlimited</strong> — Tout inclus + IA coach</div>
        </div>
      </div>

      {error && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error} — Endpoint <code className="bg-blue-100 px-1 rounded">/api/boss/users</code> à créer
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un utilisateur..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Utilisateurs Boss</h2>
          <span className="text-sm text-gray-400">{total} total</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Users className="w-8 h-8 mx-auto mb-2" />
            <p>Aucun utilisateur trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Utilisateur</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Plan</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Fin abo.</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Quiz</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Points</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Streak</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Rang</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{u.full_name}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_STYLES[u.subscription_plan]}`}>
                        {u.subscription_plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-gray-500">
                      {u.subscription_end_date
                        ? new Date(u.subscription_end_date).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold">{u.total_quizzes}</td>
                    <td className="px-4 py-3 text-center text-blue-600 font-semibold">{u.total_points.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Flame className="w-3 h-3 text-orange-400" />
                        <span className="font-semibold">{u.current_streak}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.rank ? (
                        <div className="flex items-center justify-center gap-1">
                          <Star className="w-3 h-3 text-amber-400" />
                          <span>#{u.rank}</span>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setSelectedUser(u)}
                        className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition"
                      >
                        Gérer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {total > LIMIT && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">Page {page} / {Math.ceil(total / LIMIT)}</span>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
              >Préc.</button>
              <button
                disabled={page * LIMIT >= total}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
              >Suiv.</button>
            </div>
          </div>
        )}
      </div>

      {/* User detail modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-bold text-gray-900">{selectedUser.full_name}</h2>
              <button onClick={() => setSelectedUser(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs">Plan actuel</p>
                  <p className="font-bold mt-0.5">{selectedUser.subscription_plan}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs">Fin abonnement</p>
                  <p className="font-bold mt-0.5">
                    {selectedUser.subscription_end_date
                      ? new Date(selectedUser.subscription_end_date).toLocaleDateString('fr-FR')
                      : '—'}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-blue-600 text-xs">Quiz effectués</p>
                  <p className="font-bold text-blue-800 mt-0.5">{selectedUser.total_quizzes}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-amber-600 text-xs">Points / Rang</p>
                  <p className="font-bold text-amber-800 mt-0.5">{selectedUser.total_points} pts {selectedUser.rank ? `· #${selectedUser.rank}` : ''}</p>
                </div>
              </div>

              {/* Apply promo code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Appliquer un code promo</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="CODE2025"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                  />
                  <button
                    onClick={() => applyPromo(selectedUser.id)}
                    disabled={actionLoading || !promoCode}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50 flex items-center gap-1"
                  >
                    {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Appliquer
                  </button>
                </div>
              </div>

              {/* Revoke subscription */}
              {selectedUser.is_premium && (
                <button
                  onClick={() => revokeSubscription(selectedUser.id)}
                  disabled={actionLoading}
                  className="w-full py-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-sm transition"
                >
                  Résilier l'abonnement
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BossUsers;
